import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaHome,
  FaClipboardList,
  FaUsers,
  FaSpinner,
  FaSync,
  FaChartBar,
  FaInfoCircle,
  FaListAlt,
  FaClock,
  FaEye,
} from "react-icons/fa";
// import MentorBatchQuizSummary from "./MentorBatchQuizSummary";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Quiz = {
  id: number;
  title: string;
  chapter?: string;
  level?: string;
  questionCount?: number;
};

type AssignedBatch = {
  id: string;
  quizId: number;
  quizTitle: string;
  quizLevel: string;
  batchId: number;
  batchName: string;
  assignedDate: string;
  deadline: string;
  status: string;
};

const MentorQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showBatchResultsModal, setShowBatchResultsModal] = useState(false);

  // Assigned Batches state
  const [showAssignedBatchesModal, setShowAssignedBatchesModal] =
    useState(false);
  const [assignedBatches, setAssignedBatches] = useState<AssignedBatch[]>([]);
  const [assignedBatchesLoading, setAssignedBatchesLoading] = useState(false);
  const [activeAssignedTab, setActiveAssignedTab] = useState(""); // Will be batch ID
  const [mentorBatches, setMentorBatches] = useState<any[]>([]);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/quizzes/mentor`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setQuizzes(Array.isArray(data) ? data : []);
        setLastUpdated(new Date());
      } else {
        setError("Failed to fetch quizzes");
      }
    } catch (err) {
      console.error("Error fetching quizzes:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchQuizzes();
    setRefreshing(false);
  };

  const fetchAssignedBatches = async () => {
    setAssignedBatchesLoading(true);
    try {
      // Fetch all quizzes
      const quizzesRes = await fetch(`${API_BASE_URL}/api/quizzes/mentor`, {
        credentials: "include",
      });

      if (!quizzesRes.ok) throw new Error("Failed to fetch quizzes");
      const allQuizzes = await quizzesRes.json();

      // Fetch mentor's batches
      const batchesRes = await fetch(`${API_BASE_URL}/api/mentor/my-batches`, {
        credentials: "include",
      });

      if (!batchesRes.ok) throw new Error("Failed to fetch batches");
      const batches = await batchesRes.json();
      setMentorBatches(batches);

      // Set the first batch as active tab
      if (batches.length > 0) {
        setActiveAssignedTab(batches[0].id.toString());
      }

      const allAssignments: AssignedBatch[] = [];

      // For each quiz, fetch its assignments
      for (const quiz of allQuizzes) {
        try {
          const assignRes = await fetch(
            `${API_BASE_URL}/api/quizzes/${quiz.id}/assigned-batches`,
            { credentials: "include" }
          );

          if (assignRes.ok) {
            const assignments = await assignRes.json();
            console.log(
              `✅ Assignments for quiz ${quiz.id} (${quiz.title}):`,
              assignments
            );

            // Transform the assignments - filter only mentor's batches
            assignments.forEach((assignment: any) => {
              // Check if this batch belongs to the mentor
              const batch = batches.find(
                (b: any) => b.id === assignment.batchId
              );

              if (batch) {
                const deadline = new Date(assignment.deadline);
                const now = new Date();

                // Determine status
                let status = "active";
                if (deadline < now) {
                  status = "completed";
                } else if (
                  deadline > new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
                ) {
                  status = "upcoming";
                }

                allAssignments.push({
                  id: `${quiz.id}-${
                    assignment.batchId
                  }-${Date.now()}-${Math.random()}`,
                  quizId: quiz.id,
                  quizTitle: quiz.title,
                  quizLevel: quiz.level || batch.topic || "Unspecified",
                  batchId: assignment.batchId,
                  batchName: assignment.batchName || batch.name,
                  assignedDate:
                    assignment.assignedDate || new Date().toISOString(),
                  deadline: assignment.deadline,
                  status: status,
                });
              }
            });
          }
        } catch (err) {
          console.error(
            `❌ Error fetching assignments for quiz ${quiz.id}:`,
            err
          );
        }
      }

      console.log("📊 Total loaded quiz assignments:", allAssignments.length);
      console.log("📋 Assignments:", allAssignments);
      setAssignedBatches(allAssignments);

      if (allAssignments.length === 0) {
        console.log("ℹ️ No quiz assignments found for any batch");
      }
    } catch (err) {
      console.error("❌ Failed to load assigned batches:", err);
      setAssignedBatches([]);
    } finally {
      setAssignedBatchesLoading(false);
    }
  };

  const handleViewAssignedBatches = () => {
    setShowAssignedBatchesModal(true);
    fetchAssignedBatches();
  };

  // Group quizzes by level
  const quizzesByLevel = quizzes.reduce<Record<string, Quiz[]>>((acc, quiz) => {
    const level = quiz.level || "Unspecified";
    if (!acc[level]) acc[level] = [];
    acc[level].push(quiz);
    return acc;
  }, {});

  // Sort levels: A1, A2, B1, B2, C1, C2
  const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2", "Unspecified"];
  const sortedLevels = Object.keys(quizzesByLevel).sort((a, b) => {
    const indexA = levelOrder.indexOf(a);
    const indexB = levelOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; icon: JSX.Element; className: string }
    > = {
      active: {
        label: "Active",
        icon: <FaClock />,
        className: "bg-green-100 text-green-800",
      },
      upcoming: {
        label: "Upcoming",
        icon: <FaClock />,
        className: "bg-yellow-100 text-yellow-800",
      },
      completed: {
        label: "Completed",
        icon: <FaClock />,
        className: "bg-blue-100 text-blue-800",
      },
      expired: {
        label: "Expired",
        icon: <FaClock />,
        className: "bg-red-100 text-red-800",
      },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.active;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  const renderAssignedBatchesTable = (batchId: string) => {
    // Get assignments for the selected batch
    const batchAssignments = assignedBatches.filter(
      (a) => a.batchId.toString() === batchId
    );

    if (batchAssignments.length === 0) {
      return (
        <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow">
          <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="font-semibold">
            No quiz assignments for this batch yet.
          </p>
        </div>
      );
    }

    // Always show Quiz 1 to Quiz 14 columns
    const quizColumns = Array.from({ length: 14 }, (_, i) => i + 1);

    // Sort assignments by quiz ID to map to columns
    const sortedAssignments = [...batchAssignments].sort(
      (a, b) => a.quizId - b.quizId
    );

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-30">
              <tr className="bg-gradient-to-r from-[#009fe3] to-cyan-500">
                {quizColumns.map((quizNum) => (
                  <th
                    key={quizNum}
                    className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-white border-r border-cyan-400 min-w-[120px]"
                  >
                    <span className="font-bold text-base">Quiz {quizNum}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white hover:bg-blue-50 transition-colors">
                {quizColumns.map((quizNum) => {
                  // Get the Nth quiz assignment for this batch
                  const actualAssignment = sortedAssignments[quizNum - 1];

                  return (
                    <td
                      key={`quiz${quizNum}`}
                      className="px-3 py-4 text-center border-r border-gray-200"
                    >
                      {actualAssignment ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="text-xs font-semibold text-gray-600 truncate max-w-[100px]">
                            {actualAssignment.quizTitle}
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            {new Date(
                              actualAssignment.deadline
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(
                              actualAssignment.deadline
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                            })}
                          </div>
                          <div className="mt-1">
                            {getStatusBadge(actualAssignment.status)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xl">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuizTable = (level: string, quizList: Quiz[]) => {
    return (
      <div className="mb-10" key={level}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <FaClipboardList />
            Level: {level}
          </h3>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
            {quizList.length} quizzes
          </span>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    Quiz Title
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Chapter
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {quizList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-10 text-center text-gray-400 font-semibold"
                    >
                      No quizzes found for this level.
                    </td>
                  </tr>
                ) : (
                  quizList.map((quiz, index) => (
                    <tr
                      key={quiz.id}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <p className="text-gray-900 font-medium">
                          {quiz.title}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                          {quiz.chapter || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-800">
                          {quiz.level || "Unspecified"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/mentor/manage-quizzes/${quiz.id}/assign`
                              )
                            }
                            className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-semibold"
                            title="Assign to My Batches"
                          >
                            <FaUsers size={14} />
                            Assign
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaClipboardList />}
        title="Quizzes"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Quizzes" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Info Card */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            Mentor Access
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                You can view all quizzes but can only{" "}
                <strong>assign to your own batches</strong>
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                Click <strong>"Assign"</strong> to assign quizzes to your
                batches
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>
                Click <strong>"Batch Results"</strong> to view student results
              </span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 bg-white rounded-lg shadow-lg p-4">
          <button
            onClick={handleViewAssignedBatches}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-purple-600 hover:bg-purple-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaListAlt />
            View Assigned Batches
          </button>
          <button
            onClick={() => setShowBatchResultsModal(true)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold bg-green-600 hover:bg-green-700 text-white shadow-md transition flex-1 sm:flex-initial"
          >
            <FaChartBar />
            Batch Results
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" />
            Loading quizzes...
          </div>
        ) : (
          <>
            {sortedLevels.length === 0 ? (
              <div className="text-center text-gray-400 py-10 bg-white rounded-lg shadow-lg">
                <FaClipboardList className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  No Quizzes Available
                </h3>
                <p className="mb-4">
                  No quizzes have been created yet. Please contact the admin.
                </p>
              </div>
            ) : (
              sortedLevels.map((level) =>
                renderQuizTable(level, quizzesByLevel[level])
              )
            )}
          </>
        )}
      </main>

      {/* Assigned Batches Modal */}
      {showAssignedBatchesModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          onClick={() => setShowAssignedBatchesModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAssignedBatchesModal(false)}
              className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 text-3xl font-bold bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:shadow-lg transition-all"
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header - Fixed */}
            <div className="flex-shrink-0 p-6 border-b border-gray-200 bg-white z-50">
              <h3 className="text-2xl font-bold text-[#009fe3] flex items-center gap-2">
                <FaListAlt />
                Assigned Batches & Deadlines
              </h3>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {assignedBatchesLoading ? (
                <div className="text-center text-[#009fe3] flex items-center justify-center gap-2 py-10">
                  <FaSpinner className="animate-spin" />
                  Loading assigned batches...
                </div>
              ) : (
                <>
                  {/* Batch Tabs - Sticky */}
                  <div className="sticky top-0 bg-white z-40 pb-4 mb-6 border-b border-gray-200">
                    {mentorBatches.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No batches available</p>
                      </div>
                    ) : (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {mentorBatches.map((batch) => {
                          const count = assignedBatches.filter(
                            (a) => a.batchId === batch.id
                          ).length;
                          return (
                            <button
                              key={batch.id}
                              onClick={() =>
                                setActiveAssignedTab(batch.id.toString())
                              }
                              className={`px-4 py-2 rounded-t-lg font-semibold text-sm whitespace-nowrap transition-colors ${
                                activeAssignedTab === batch.id.toString()
                                  ? "bg-[#009fe3] text-white"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                            >
                              {batch.name}
                              {count > 0 && (
                                <span
                                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                                    activeAssignedTab === batch.id.toString()
                                      ? "bg-white/20"
                                      : "bg-gray-300"
                                  }`}
                                >
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Table for Active Batch */}
                  {activeAssignedTab &&
                    renderAssignedBatchesTable(activeAssignedTab)}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Results Modal */}
      {showBatchResultsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setShowBatchResultsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBatchResultsModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-6 text-[#009fe3] flex items-center gap-2">
              <FaChartBar />
              Batch Quiz Results Summary
            </h3>
            {/* TODO: Add MentorBatchQuizSummary component when available */}
            <div className="text-center py-10 text-gray-500">
              <p>Batch results summary will be displayed here.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorQuizPage;
